

import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import axios from 'axios';
import ArchivedHeader from './ArchivedHeader';
import ArchivedFilters, { DEFAULT_FILTERS } from './ArchivedFilters';
import { HiMailOpen } from 'react-icons/hi';
import { toast } from 'react-toastify';
import DeletePopup from '../DeletePopup';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import services from '../../services';
import TablePagination from '../../common/TablePagination';
import { getLocalStorageItem } from '../../utils/Localstorage/localStorageUtils';

const ArchivedBoard = () => {
    const [allTasks, setAllTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedReOpenBoard, setSelectedReOpenBoard] = useState('');
    const [availableBoards, setAvailableBoards] = useState([]);
    const [archivePage, setArchivePage] = useState(1);
    const [archiveTotalPages, setArchiveTotalPages] = useState(1);
    const [boardColumn, setBoardColumn] = useState([]);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [searchInput, setSearchInput] = useState('');
    const [defaultBoardApplied, setDefaultBoardApplied] = useState(false);
    const [showFilterPanel,setShowFilterPanel] = useState(false);
    const [modalState, setModalState] = useState({
        isOpen: false,
        action: null,
        item: null,
        title: '',
        description: ''
    });

    const [showArchiveOpen, setShowArchiveOpen] = useState(false);
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const isArchivedView = location.state?.isformArchivedView || false;
    const boardName = location.state?.boardName || '';

    const fetchArchivedTasks = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit: 20,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                createdAtFilter: filters.createdAtFilter,
            };
            if (filters.search) params.search = filters.search;
            if (filters.boardId.length) params.boardId = filters.boardId.join(',');
            if (filters.createdAtFilter === 'customRange') {
                if (filters.startDate) params.startDate = filters.startDate;
                if (filters.endDate) params.endDate = filters.endDate;
            }

            const advancedFilters = [];
            (filters.advanced || []).forEach((f) => {
                if (!f.field || !f.value) return;
                if (f.field === 'user.name' && Array.isArray(f.value)) {
                    const names = f.value.map((u) => u.label).filter(Boolean);
                    if (names.length) advancedFilters.push({ field: f.field, value: names.join(',') });
                } else if (f.field === 'customProperty' && Array.isArray(f.value)) {
                    const ids = f.value.map((p) => p.value).filter(Boolean);
                    if (ids.length) {
                        const optionIds = Array.isArray(f.optionValue) ? f.optionValue.map((o) => o.value).filter(Boolean) : [];
                        advancedFilters.push({
                            field: f.field,
                            value: ids.join(','),
                            ...(optionIds.length > 0 && { optionValue: optionIds.join(',') }),
                        });
                    }
                } else if (typeof f.value === 'string' && f.value.trim() !== '') {
                    advancedFilters.push({ field: f.field, value: f.value.trim() });
                }
            });
            if (advancedFilters.length) params.filters = JSON.stringify(advancedFilters);

            const isFilterApplied =
                advancedFilters.length > 0 ||
                !!params.search ||
                filters.createdAtFilter !== DEFAULT_FILTERS.createdAtFilter ||
                filters.sortBy !== DEFAULT_FILTERS.sortBy ||
                filters.sortOrder !== DEFAULT_FILTERS.sortOrder;
            if (isFilterApplied) params.limit = 1000;

            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/task/archieveTask`, { params });
            const archivedData = response.data.data;

            let tasksList = [];
            if (Array.isArray(archivedData) && archivedData.length > 0 && archivedData[0]?.tasks) {
                tasksList = archivedData.reduce((acc, boardData) => {
                    if (boardData.tasks && boardData.tasks.length > 0) {
                        const tasksWithBoardName = boardData.tasks.map((task) => ({
                            ...task,
                            boardName: task.boardName || boardData.boardName,
                            boardId: task.boardId || boardData.boardId,
                        }));
                        return acc.concat(tasksWithBoardName);
                    }
                    return acc;
                }, []);
            } else if (Array.isArray(archivedData)) {
                tasksList = archivedData;
            }

            setAllTasks(tasksList);
            const totalPages = response.data?.pagination?.totalPages
                || response.data?.totalPages
                || response.data?.totalPage
                || 1;
            setArchiveTotalPages(totalPages);
        } catch (error) {
            console.error('Error fetching archived tasks:', error);
            setAllTasks([]);
        } finally {
            setIsLoading(false);
        }
    }, [filters]);

    const fetchAllBoards = useCallback(async () => {
        try {
            const userDataRaw = getLocalStorageItem('userData');
            const userData = typeof userDataRaw === 'string' ? JSON.parse(userDataRaw) : userDataRaw;

            let boards = [];
            if (userData?.role === 'User') {
                const response = await services.userassignboard.GET_UserAssignBoard_ID(userData?.id);
                const assignedBoards = response?.data?.data || [];
                boards = assignedBoards
                    .filter((item) => item?.Board)
                    .map((item) => ({ id: item.Board.id, boardName: item.Board.boardName, board: item.Board }));
            } else {
                const { data } = await services.board.GETALLBOARD();
                boards = (data || []).map((board) => ({ id: board.id, boardName: board.boardName, board }));

                try {
                    const { data: deletedData } = await services.board.GET_DELETED_BOARDS();
                    const deletedBoards = (deletedData || []).map((board) => ({
                        id: board.id,
                        boardName: `${board.boardName} (Deleted)`,
                        board,
                        isDeleted: true,
                    }));
                    boards = boards.concat(deletedBoards);
                } catch (deletedError) {
                    console.error('Error fetching deleted boards:', deletedError);
                }
            }

            setAvailableBoards(boards);
        } catch (error) {
            console.error('Error fetching boards:', error);
            setAvailableBoards([]);
        }
    }, []);

    useEffect(() => {
        fetchAllBoards();
    }, [fetchAllBoards]);

    useEffect(() => {
        if (defaultBoardApplied || availableBoards.length === 0) return;
        setFilters((prev) => (prev.boardId.length === 0 ? { ...prev, boardId: [availableBoards[0].id] } : prev));
        setDefaultBoardApplied(true);
    }, [availableBoards, defaultBoardApplied]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const prevFiltersRef = React.useRef(filters);

    useEffect(() => {
        if (!defaultBoardApplied) return;
        if (prevFiltersRef.current !== filters) {
            prevFiltersRef.current = filters;
            if (archivePage !== 1) {
                setArchivePage(1);
                return;
            }
        }
        fetchArchivedTasks(archivePage);
    }, [archivePage, fetchArchivedTasks, filters, defaultBoardApplied]);

    useEffect(() => {
        setSelectedTaskIds([]);
    }, [allTasks]);

    const toggleTaskSelected = (taskId) => {
        setSelectedTaskIds((prev) =>
            prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
        );
    };

    const isAllSelected = allTasks.length > 0 && selectedTaskIds.length === allTasks.length;

    const toggleSelectAll = () => {
        setSelectedTaskIds(isAllSelected ? [] : allTasks.map((task) => task.id));
    };

    const clearSelection = () => setSelectedTaskIds([]);

    const handleBulkDeleteClick = () => {
        if (selectedTaskIds.length === 0) return;
        setModalState({
            isOpen: true,
            action: 'bulkDelete',
            item: selectedTaskIds,
            title: `Are you sure you want to delete ${selectedTaskIds.length} selected task(s)?`,
            description: 'This will permanently delete the selected tasks from the archive. This action cannot be undone.',
        });
    };

    const handleTaskClick = (task) => {
        navigate(`/detail/${task.id}`, {
            state: {
                selectedMail: task,
                taskID: task.id,
                clickMailTask: true,
                type: task.taskType || 'Gmail',
                openTaskDataSidebar: true,
                currentTask: task,
                boardId: task.boardId,
                isArchived: true,
                isformArchivedView: true,
                boardName: boardName
            },
        });
    };
    const [taskDetails, setTaskDetails] = useState()
    const handleReopenTask = async (task, type, boardObj) => {
        if (!task.boardListId || !task.boardId) {
            toast.error('Cannot reopen task: missing board or column information.');
            return;
        }

        if (type == "isBoardSelected") {
            setModalState({
                isOpen: true,
                action: 'reopen',
                item: { ...task, boardId: selectedReOpenBoard, boardListId: selectedColumn },
                title: 'Are you sure you want to reopen this task?',
                description: `This will move the task back to the board ${boardObj.label}.`
            });
            setShowArchiveOpen(false)
        } else {
            setShowArchiveOpen(true)
            setTaskDetails(task)
        }


    };

    const handleSleepTask = (taskId) => {
        setModalState({
            isOpen: true,
            action: 'sleep',
            item: taskId,
            title: 'Are you sure you want to sleep this task?',

        });
    };

    const handleDeleteTask = (taskId) => {
        setModalState({
            isOpen: true,
            action: 'delete',
            item: taskId,
            title: 'Are you sure you want to delete this task?',
            description: 'This will permanently delete the task from the archive. This action cannot be undone.',
        });
    };

    const confirmAction = async () => {
        const { action, item } = modalState;
        if (action === 'reopen') {
            try {
                const data = {
                    boardListId: item.boardListId,
                    boardId: item.boardId,
                    isInComplete: false,
                };
                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/task/updateTask/${item.id}`, data);
                toast.success('Task has been reopened.');
                setAllTasks((prevTasks) => prevTasks.filter((t) => t.id !== item.id));
            } catch (error) {
                console.error('Error reopening task:', error);
                toast.error('Failed to reopen task.');
            }
        } else if (action === 'sleep') {
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/task/deleteTask/${item}`);
                toast.success('Task moved to sleep successfully');
                setAllTasks((prevTasks) => prevTasks.filter((task) => task.id !== item));
            } catch (error) {
                console.error('Error deleting task:', error);
                fetchArchivedTasks(archivePage);
            }
        } else if (action === 'delete') {
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/task/archieveTask/${item}`);
                toast.success('Task deleted successfully');
                setAllTasks((prevTasks) => prevTasks.filter((task) => task.id !== item));
            } catch (error) {
                console.error('Error deleting archived task:', error);
                toast.error('Failed to delete task.');
            }
        } else if (action === 'bulkDelete') {
            const ids = item;
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/task/archieveTask/bulk`, {
                    data: { ids },
                });
                toast.success('Selected tasks deleted successfully');
                setAllTasks((prevTasks) => prevTasks.filter((task) => !ids.includes(task.id)));
            } catch (error) {
                console.error('Error bulk deleting archived tasks:', error);
                toast.error('Failed to delete selected tasks.');
            }
            setSelectedTaskIds([]);
        }
        closeModal();
    };

    const closeModal = () => {
        setModalState({ isOpen: false, action: null, item: null, title: '', description: '' });
    };

    const handleClose = () => setShowArchiveOpen(false);
    const handleReOpenBoardChange = (e) => {
        const boardId = e.target.value;
        setSelectedReOpenBoard(boardId);

        if (boardId) {
            getBoardColumn(boardId);
        }
    };

    const [selectedColumn, setSelectedColumn] = useState("");

    const handleColumnChange = (e) => {
        setSelectedColumn(e.target.value);

    };

    const getBoardColumn = async (id) => {
        try {
            const response = await services.board.SELECTED_BOARD(id);
            const data = response?.data?.map((item) => ({
                value: item?.id,
                label: item?.coloum,
            }));
            setBoardColumn(data);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <ArchivedHeader
                isArchivedView={isArchivedView}
                boardIds={filters.boardId}
                availableBoards={availableBoards}
                onBoardIdsChange={(ids) => setFilters((prev) => ({ ...prev, boardId: ids }))}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                filtersOpen={showFilterPanel}
                onToggleFilters={() => setShowFilterPanel((prev) => !prev)}
            />
            {showFilterPanel && (
                <ArchivedFilters
                    filters={filters}
                    setFilters={setFilters}
                    onSearchClear={() => setSearchInput('')}
                />
            )}
            <div className="flex-grow p-4 overflow-y-auto">
                {allTasks.length > 0 && (
                    <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={toggleSelectAll}
                                style={{ accentColor: '#1a7d3e', width: '15px', height: '15px', cursor: 'pointer' }}
                            />
                            Select all
                        </label>
                        <button
                            type="button"
                            onClick={handleBulkDeleteClick}
                            disabled={selectedTaskIds.length === 0}
                            className={
                                "px-3 py-1 rounded text-sm font-semibold border "
                                + (selectedTaskIds.length === 0
                                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "text-red-600 border-red-200 hover:bg-red-50")
                            }
                        >
                            Delete selected{selectedTaskIds.length > 0 ? ` (${selectedTaskIds.length})` : ''}
                        </button>
                        {selectedTaskIds.length > 0 && (
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="px-3 py-1 rounded text-sm font-semibold text-gray-600 hover:bg-gray-100"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
                {isLoading ? (
                    <div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
                        <span className="loader w-10 h-10 border-4 border-t-transparent rounded-full animate-spin border-[#1a7d3e]"></span>
                    </div>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {allTasks.length > 0 ? (
                        allTasks.map((task) => (
                            <div
                                key={task.id}
                                onClick={() => handleTaskClick(task)}
                                style={{
                                    background: '#f6f9fd',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    border: '1px solid #e3e8ee',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    fontSize: '14px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    cursor: 'pointer',
                                }}
                            >
                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedTaskIds.includes(task.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => toggleTaskSelected(task.id)}
                                            style={{ marginTop: '3px', flexShrink: 0, cursor: 'pointer', accentColor: '#1a7d3e', width: '15px', height: '15px' }}
                                        />
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                flexGrow: 1,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {task?.taskType === 'Gmail' && (
                                                <HiMailOpen style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                                            )}
                                            {task?.taskType === 'Whatsapp' && (
                                                <img
                                                    src="assets/whatsapp.svg"
                                                    alt="whatsapp"
                                                    style={{ width: '16px', height: '16px', flexShrink: 0 }}
                                                />
                                            )}
                                            <div
                                                style={{
                                                    color: '#222',
                                                    fontWeight: 500,
                                                    textAlign: 'start',
                                                    whiteSpace: 'pre-line',
                                                    overflowWrap: 'break-word',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                {task.title || task.subject || 'Untitled Task'}
                                            </div>
                                        </div>
                                        <span
                                            style={{
                                                color: '#888',
                                                fontSize: '13px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {moment(task.updatedAt).format('MMM DD')}
                                        </span>
                                    </div>

                                    <div className="text-xs text-gray-500">Board: {task.boardName}</div>


                                    {task.SubTasks && task.SubTasks.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-gray-200 ml-4">
                                            {task.SubTasks.map((subTask) => (
                                                <div key={subTask.id} className="flex items-center gap-2 mt-1">
                                                    {subTask?.taskType === 'Gmail' ? (
                                                        <HiMailOpen className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                                    ) : subTask?.taskType === 'Whatsapp' ? (
                                                        <img
                                                            src="assets/whatsapp.svg"
                                                            alt="whatsapp"
                                                            className="w-4 h-4 flex-shrink-0"
                                                        />
                                                    ) : null}
                                                    <span className="text-sm text-gray-700">{subTask.title || subTask.subject || 'Untitled Sub-task'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>


                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                        marginTop: '10px',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div
                                            title="Sleep"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSleepTask(task.id);
                                            }}
                                            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25 2.25 6.615 2.25 12s4.365 9.75 9.75 9.75z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M9 8.25h2.25M12.75 8.25H15M9 12h2.25M12.75 12H15m-3.75 3.75h2.25M12.75 15.75H15"
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            data-reopen-button="true"
                                            title="Reopen"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReopenTask(task);
                                            }}
                                            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                style={{ pointerEvents: 'none' }}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            title="Delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteTask(task.id);
                                            }}
                                            style={{ cursor: 'pointer', width: '20px', height: '20px', color: '#ef4444' }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    {task.User && (
                                        <div
                                            style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '50%',
                                                backgroundColor: '#512da8',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                            }}
                                            title={task.User.name}
                                        >
                                            {task.User.name.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="col-span-4 text-center text-gray-500">
                            No archived tickets found for the selected board.
                        </p>
                    )}
                </div>
                )}
                {archiveTotalPages > 1 && (
                    <TablePagination
                        currentPage={archivePage}
                        totalPages={archiveTotalPages}
                        onPageChange={setArchivePage}
                    />
                )}
            </div>
            {modalState.isOpen && (
                <DeletePopup
                    open={modalState.isOpen}
                    title={modalState.title}
                    description={modalState.description}
                    onConfirm={confirmAction}
                    onCancel={closeModal}
                />
            )}

            {showArchiveOpen && (
                <>
                    <Modal
                        show={showArchiveOpen}
                        onHide={handleClose}
                        centered
                        backdrop="static"
                        keyboard={false}
                        contentClassName="p-2 rounded-4"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600', color: '#333' }}>
                                Select a Board
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <div className="pt-1 pb-4  bg-white ">
                                <select
                                    value={selectedReOpenBoard}
                                    onChange={(e) => handleReOpenBoardChange(e)}
                                    className="archived-board-select p-2 w-full border rounded-md shadow-sm"
                                >
                                    <option value="">Choose Board</option>
                                    {boardName && boardName?.length > 0 && boardName.map((board) => (
                                        <option key={board.value} value={board?.value}>
                                            {board.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="pt-1 pb-4 bg-white border-b">
                                <select
                                    value={selectedColumn}
                                    onChange={handleColumnChange}
                                    className="archived-board-select p-2 w-full border rounded-md shadow-sm"
                                >
                                    <option value="">Choose Column</option>
                                    {boardColumn.map((column) => (
                                        <option key={column.value} value={column.value}>
                                            {column.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                        </Modal.Body>

                        <Modal.Footer>

                            <div className="bg-white px-1 pb-3 flex gap-2 items-center justify-center sm:px-6">

                                <button
                                    type="button"
                                    className="inline-flex justify-center rounded bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm border border-gray-300 hover:bg-gray-50 font-google"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={
                                        "inline-flex justify-center rounded bg-[#42B4F4] px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#42B4F4] border-0 font-google"
                                        + ((selectedReOpenBoard && selectedColumn) ? "" : " opacity-60 cursor-not-allowed")
                                    }
                                    onClick={() => {
                                        if (selectedReOpenBoard && selectedColumn) {
                                            const selectedBoardObj = boardName.find(board => board.value === selectedReOpenBoard);
                                            handleReopenTask(taskDetails, "isBoardSelected", selectedBoardObj);
                                        }
                                    }}
                                    disabled={!(selectedReOpenBoard && selectedColumn)}
                                >
                                    Confirm
                                </button>
                            </div>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </div>
    );
};

export default ArchivedBoard;
